import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  ContentCopy as ContentCopyIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';

import StandardPageLayout from '../../components/shared/StandardPageLayout';
import ConfirmationModal from '../../components/ConfirmationModal';
import QRCodeDialog from '../../components/QrCodeDialog';
import toastUtils from '../../utils/toastUtils';
import { AuthContext } from '../../context/Auth/AuthContext';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';

const LandingPagesList = () => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const companyId = localStorage.getItem("companyId") ? localStorage.getItem("companyId") : user?.companyId;
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [landingPages, setLandingPages] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [viewMode, setViewMode] = useState(localStorage.getItem('landingPagesViewMode') || 'list');
  
  // Função para criar nova landing page
  const handleCreateNewPage = () => {    
    localStorage.setItem("landingPageNew", "true");
    history.push('/landing-pages/edit/new');
  };
  
  // Função para carregar as landing pages
  const loadLandingPages = useCallback(async () => {
    try {
      setLoading(true);
      
      let queryParams = `page=${page + 1}&limit=${rowsPerPage}`;
      
      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }
      
      if (filterActive !== null) {
        queryParams += `&active=${filterActive}`;
      }
      
      const response = await api.get(`/landing-pages?${queryParams}`);
      
      setLandingPages(response.data.data || []);
      setTotalPages(response.data.total || 0);
      
    } catch (error) {
      console.error('Erro ao carregar landing pages:', error);
      toastUtils.error('Erro na Página', i18n.t('landingPages.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filterActive]);
  
  // Carregar landing pages na inicialização e quando os filtros mudarem
  useEffect(() => {
    loadLandingPages();
  }, [loadLandingPages]);
  

  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Função para copiar URL da landing page
  const handleCopyUrl = (slug) => {
    if (!companyId) {
      toastUtils.error('Erro na Página', i18n.t('landingPages.messages.companyIdNotFound'));
      return;
    }
    const url = `${window.location.origin}/l/${companyId}/${slug}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toastUtils.success('Página Copiada', i18n.t('landingPages.messages.urlCopied'));
      })
      .catch((error) => {
        toastUtils.error('Erro na Página', i18n.t('landingPages.messages.urlCopyError'));
      });
  };

  // Funções para manipular o diálogo de exclusão
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };
  
  // Função para excluir uma landing page
  const handleDelete = async () => {
    try {
      await api.delete(`/landing-pages/${deleteId}`);
      toastUtils.success('Página Excluída', i18n.t('landingPages.messages.deleteSuccess'));
      loadLandingPages();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir landing page:', error);
      toastUtils.error('Erro na Página', i18n.t('landingPages.messages.deleteError'));
    }
  };
  
  // Funções para manipular o diálogo de QR Code
  const handleOpenQRCodeDialog = (page) => {
    setSelectedPage(page);
    setQrCodeDialogOpen(true);
  };
  
  const handleCloseQRCodeDialog = () => {
    setQrCodeDialogOpen(false);
    setSelectedPage(null);
  };
  
  // Função para alternar o status ativo/inativo
  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/landing-pages/${id}/toggle-active`);
      const statusText = currentStatus ? i18n.t('landingPages.messages.deactivated') : i18n.t('landingPages.messages.activated');
      toastUtils.success('Página Atualizada', i18n.t('landingPages.messages.statusChangeSuccess', { status: statusText }));
      loadLandingPages();
    } catch (error) {
      console.error('Erro ao alterar status da landing page:', error);
      toastUtils.error('Erro na Página', i18n.t('landingPages.messages.statusChangeError'));
    }
  };
  
  // Função para alternar entre modo de exibição
  const handleToggleViewMode = () => {
    const newMode = viewMode === 'list' ? 'grid' : 'list';
    setViewMode(newMode);
    localStorage.setItem('landingPagesViewMode', newMode);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset para primeira página ao pesquisar
  };

  // Filtros para o cabeçalho
  const renderFilters = () => (
    <Box display="flex" alignItems="center" gap={2}>
      <FormControl size="small">
        <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
          {i18n.t('landingPages.status')}
        </FormLabel>
        <Select
          value={filterActive === null ? 'all' : filterActive.toString()}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'all') {
              setFilterActive(null);
            } else {
              setFilterActive(value === 'true');
            }
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="all">{i18n.t('landingPages.all')}</MenuItem>
          <MenuItem value="true">{i18n.t('landingPages.active')}</MenuItem>
          <MenuItem value="false">{i18n.t('landingPages.inactive')}</MenuItem>
        </Select>
      </FormControl>

      <Tooltip title={viewMode === 'list' ? i18n.t('landingPages.viewAsGrid') : i18n.t('landingPages.viewAsList')}>
        <IconButton onClick={handleToggleViewMode} color="primary">
          {viewMode === 'list' ? <ViewModuleIcon /> : <ViewListIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
  
  // Renderizar conteúdo principal
  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (landingPages.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
          <QrCodeIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom color="textSecondary">
            {search ? i18n.t('landingPages.noLandingPagesFound') : i18n.t('landingPages.noLandingPagesCreated')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {search 
              ? i18n.t('landingPages.adjustSearch') 
              : i18n.t('landingPages.createFirstLandingPage')
            }
          </Typography>
          {!search && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateNewPage}
            >
              {i18n.t('landingPages.createNewLandingPage')}
            </Button>
          )}
        </Box>
      );
    }

    return (
      <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t('landingPages.table.title')}</TableCell>
              <TableCell>{i18n.t('landingPages.table.url')}</TableCell>
              <TableCell align="center">{i18n.t('landingPages.table.status')}</TableCell>
              <TableCell align="center">{i18n.t('landingPages.table.qrCode')}</TableCell>
              <TableCell align="center">{i18n.t('landingPages.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {landingPages.map((page) => (
              <TableRow key={page.id} hover>
                <TableCell>
                  <Typography variant="body1" fontWeight={500}>
                    {page.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      /{companyId}/{page.slug}
                    </Typography>
                    <Tooltip title={i18n.t('landingPages.actions.copyUrl')}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyUrl(page.slug)}
                        color="primary"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={page.active ? i18n.t('landingPages.actions.deactivate') : i18n.t('landingPages.actions.activate')}>
                    <Chip 
                      label={page.active ? i18n.t('landingPages.status.active') : i18n.t('landingPages.status.inactive')}
                      size="small"
                      color={page.active ? "success" : "default"}
                      variant={page.active ? "filled" : "outlined"}
                      onClick={() => handleToggleActive(page.id, page.active)}
                      sx={{ minWidth: '70px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={i18n.t('landingPages.actions.viewQrCode')}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenQRCodeDialog(page)}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Tooltip title={i18n.t('landingPages.actions.edit')}>
                      <IconButton
                        color="primary"
                        onClick={() => history.push(`/landing-pages/edit/${page.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t('landingPages.actions.delete')}>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(page.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {/* Paginação */}
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
              {i18n.t('landingPages.pagination.itemsPerPage')}
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Typography variant="body2" color="textSecondary">
            {i18n.t('landingPages.pagination.showing', { showing: landingPages.length, total: totalPages })}
          </Typography>
        </Box>
      </TableContainer>
    );
  };

  // Configuração das ações do cabeçalho
  const pageActions = [
    {
      label: i18n.t('landingPages.newLandingPage'),
      icon: <AddIcon />,
      onClick: handleCreateNewPage,
      variant: "contained",
      color: "primary",
      tooltip: i18n.t('landingPages.createNewLandingPage')
    }
  ];

  return (
    <>
      <StandardPageLayout
        title={i18n.t('landingPages.title')}
        actions={pageActions}
        searchValue={search}
        onSearchChange={handleSearch}
        searchPlaceholder={i18n.t('landingPages.searchPlaceholder')}
        showSearch={true}
        loading={loading}
      >
        {/* Filtros adicionais */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          {renderFilters()}
        </Box>

        {renderContent()}
      </StandardPageLayout>
      
      {/* Modal de confirmação de exclusão */}
      {deleteDialogOpen && (
        <ConfirmationModal
          title={i18n.t('landingPages.modals.deleteTitle')}
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDelete}
        >
          <Typography>
            {i18n.t('landingPages.modals.deleteMessage')}
          </Typography>
        </ConfirmationModal>
      )}
      
      {/* Diálogo de QR Code */}
      <QRCodeDialog
        open={qrCodeDialogOpen}
        landingPage={selectedPage}
        onClose={handleCloseQRCodeDialog}
      />
    </>
  );
};

export default LandingPagesList;